import { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import useAttentionPulse, { PULS_KLASSE } from '../../shared/useAttentionPulse'

import styles from './style.module.scss'

/**
 * Werkzeugleiste für Listen: Suche, Filter, Sortierung, Ansichtswechsel.
 *
 * Beide neuen Ansichten — Anforderungen und Änderungsliste — brauchen
 * dasselbe. Eine gemeinsame Leiste hält sie in Aussehen und Verhalten
 * gleich; zwei Nachbauten wären nach der ersten Änderung auseinandergelaufen.
 *
 * Zurücksetzen gibt es zweimal, und das ist Absicht: ein Kreuz direkt im
 * Suchfeld für den Suchtext allein, und ein „Alles zurücksetzen“ für Suche
 * und Filter zusammen. Wer nur die Suche leeren will, verliert damit nicht
 * seine eingestellten Filter. Bringt eine Seite ihre eigene Stelle dafür mit,
 * schaltet `showReset={false}` den zweiten ab.
 */
export default function ListToolbar(props) {
	const activeFilters =
		props.filters.reduce((sum, filter) => sum + (props.filterValues[filter.key]?.length || 0), 0) +
		props.extraActive
	const hasSearch = Boolean(props.search)
	const canReset = hasSearch || activeFilters > 0

	// Der Filter-Abschnitt kann optional ein- und ausgeklappt werden. Wo das
	// nicht gewünscht ist (Anforderungen), bleibt er wie bisher immer sichtbar.
	// Standardmäßig eingeklappt, damit die Kopfzeile ruhig bleibt; eine aktive
	// Auswahl zeigt der Umschalter als Zähler, damit nichts unbemerkt filtert.
	const [filtersOpen, setFiltersOpen] = useState(false)
	const puls = useAttentionPulse(activeFilters > 0)
	// „Filter" umfasst sowohl die Chip-Gruppen als auch zusaetzliche, vom Aufrufer
	// gerenderte Filter (children, z. B. eine Auswahl mit Suche).
	const hasFilterRow = Boolean(props.filters.length) || Boolean(props.children)
	const collapsible = props.collapsibleFilters && hasFilterRow
	const filtersVisible = hasFilterRow && (!collapsible || filtersOpen)

	const toggleValue = (key, value) => {
		const current = props.filterValues[key] || []
		const next = current.includes(value) ? current.filter(item => item !== value) : [...current, value]

		props.onFilterChange({ ...props.filterValues, [key]: next })
	}

	return (
		<div className={clsx(styles.root, props.className)}>
			<div className={styles.topRow}>
				{/* Wo die Suche in der Kopfleiste der Seite steht (Entwicklungen),
				    bleibt sie hier weg — zweimal dasselbe Feld waere zweimal
				    dieselbe Frage mit womoeglich zwei Antworten. */}
				{props.showSearch && (
					<div className={styles.searchWrap}>
						<input
							type="search"
							className={styles.search}
							value={props.search}
							placeholder={props.searchPlaceholder}
							onChange={event => props.onSearchChange(event.target.value)}
						/>
						{hasSearch && (
							<button
								type="button"
								className={styles.searchClear}
								title="Suche leeren"
								aria-label="Suche leeren"
								onClick={() => props.onSearchChange('')}
							>
								×
							</button>
						)}
					</div>
				)}

				{collapsible && (
					/*
					 * Steht ein Filter, leuchtet der Umschalter beim Anzeigen kurz
					 * auf. Eingeklappt ist die Zahl daneben der einzige Hinweis
					 * darauf, dass die Liste nur einen Ausschnitt zeigt — und den
					 * uebersieht man, wenn man die Seite gerade erst geoeffnet hat.
					 */
					<button
						type="button"
						className={clsx(
							styles.filterToggle,
							filtersOpen && styles.filterToggleOpen,
							puls.leuchtet && PULS_KLASSE
						)}
						aria-expanded={filtersOpen}
						onAnimationEnd={puls.onAnimationEnd}
						onClick={() => setFiltersOpen(open => !open)}
					>
						Filter
						{activeFilters > 0 && <span className={styles.filterToggleBadge}>{activeFilters}</span>}
						<span className={styles.filterToggleCaret} aria-hidden="true">
							{filtersOpen ? '▲' : '▼'}
						</span>
					</button>
				)}

				{Boolean(props.sortOptions.length) && (
					<label className={styles.sortWrap}>
						<span className={styles.sortLabel}>Sortierung</span>
						<select
							className={styles.select}
							value={props.sort}
							onChange={event => props.onSortChange(event.target.value)}
						>
							{props.sortOptions.map(option => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</label>
				)}

				<div className={styles.viewToggle}>
					<button
						type="button"
						className={clsx(styles.viewBtn, props.view === 'list' && styles.viewBtnActive)}
						onClick={() => props.onViewChange('list')}
					>
						Liste
					</button>
					<button
						type="button"
						className={clsx(styles.viewBtn, props.view === 'tiles' && styles.viewBtnActive)}
						onClick={() => props.onViewChange('tiles')}
					>
						Kacheln
					</button>
				</div>
			</div>

			{filtersVisible && (
				<div className={styles.filterRow}>
					{props.filters.map(filter => (
						<div key={filter.key} className={styles.filterGroup}>
							<span className={styles.filterLabel}>{filter.label}</span>
							<div className={styles.chips}>
								{filter.options.map(option => {
									const active = (props.filterValues[filter.key] || []).includes(option.value)

									return (
										<button
											key={option.value}
											type="button"
											className={clsx(styles.chip, active && styles.chipActive)}
											onClick={() => toggleValue(filter.key, option.value)}
										>
											{option.label}
											{active && <span className={styles.chipX}>×</span>}
										</button>
									)
								})}
							</div>
						</div>
					))}
					{/* Zusaetzliche Filter, die keine Chip-Liste sind (z. B. eine
					    Auswahl mit Suche). Kommen vom Aufrufer fertig gerendert. */}
					{props.children}
				</div>
			)}

			<div className={styles.statusRow}>
				<span className={styles.count}>
					{props.count === null ? 'Wird geladen …' : `${props.count} ${props.countLabel}`}
				</span>
				{canReset && props.showReset && (
					<button type="button" className={styles.reset} onClick={props.onReset}>
						Alles zurücksetzen
						{activeFilters > 0 && ` (${activeFilters} Filter)`}
					</button>
				)}
			</div>
		</div>
	)
}

ListToolbar.propTypes = {
	className: PropTypes.string,
	search: PropTypes.string.isRequired,
	onSearchChange: PropTypes.func.isRequired,
	searchPlaceholder: PropTypes.string,
	// Wenn false, bleibt das Suchfeld weg (die Seite hat ihr eigenes).
	showSearch: PropTypes.bool,
	filters: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			options: PropTypes.array.isRequired
		})
	),
	filterValues: PropTypes.object,
	onFilterChange: PropTypes.func.isRequired,
	// Wenn true, lässt sich der Filter-Abschnitt ein- und ausklappen.
	collapsibleFilters: PropTypes.bool,
	sort: PropTypes.string,
	sortOptions: PropTypes.array,
	onSortChange: PropTypes.func,
	view: PropTypes.oneOf(['list', 'tiles']).isRequired,
	onViewChange: PropTypes.func.isRequired,
	onReset: PropTypes.func.isRequired,
	/*
	 * Wo die Seite eine eigene Stelle zum Zurücksetzen hat (Anforderungen: die
	 * Leiste „Aktive Filter"), bleibt der Link hier weg — zwei Knöpfe mit
	 * demselben Zweck an zwei Stellen sind einer zu viel.
	 */
	showReset: PropTypes.bool,
	count: PropTypes.number,
	countLabel: PropTypes.string,
	// Zusaetzliche, nicht-Chip-Filter (fertig gerendert vom Aufrufer).
	children: PropTypes.node,
	// Aktive Filter, die nicht in `filters` stecken (Funnel, Auswahl mit Suche),
	// damit „Zuruecksetzen" auch sie zaehlt und sichtbar bleibt.
	extraActive: PropTypes.number
}

ListToolbar.defaultProps = {
	className: '',
	searchPlaceholder: 'Suchen',
	showSearch: true,
	collapsibleFilters: false,
	showReset: true,
	filters: [],
	filterValues: {},
	sortOptions: [],
	sort: '',
	onSortChange: () => {},
	count: null,
	countLabel: 'Einträge',
	children: null,
	extraActive: 0
}
